"use client"

import * as React from "react"
import { Link } from "react-router"
import { cn } from "@/lib/utils"
import { daysUntil } from "@/lib/evaluation-term-utils"
import {
  evaluationCoveragePercent,
  EVALUATION_REFERENCE_DATE,
  evaluationCoverageCount,
  type EvaluationTerm,
} from "@/lib/mock/evaluation-dashboard"
import {
  evaluationDistributionPath,
  evaluationReviewPath,
  evaluationSchedulePath,
} from "@/lib/evaluation-routes"
import { useProduct } from "@/contexts/product-context"
import { Button } from "@/components/ui/button"
import {
  EvaluationStagePipeline,
  ResponseTargetBar,
} from "./evaluation-stage-pipeline"

function CoverageBar({
  percent,
  covered,
  total,
  complete,
  notSetup,
  draft,
}: {
  percent: number
  covered: number
  total: number
  complete?: boolean
  notSetup: number
  draft: number
}) {
  const setupGap = notSetup + draft

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="font-medium text-foreground">Evaluation coverage</span>
        <span className="tabular-nums text-muted-foreground">
          {complete ? (
            <span className="inline-flex items-center gap-1 text-sm text-chart-2">
              <i className="fa-solid fa-circle-check text-sm" aria-hidden="true" />
              Complete
            </span>
          ) : (
            `${percent}% · ${covered}/${total}`
          )}
        </span>
      </div>
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Evaluation coverage ${percent} percent`}
      >
        <div
          className={cn(
            "h-full rounded-full transition-all",
            complete ? "bg-chart-2" : "bg-brand",
          )}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      {!complete && setupGap > 0 ? (
        <p className="text-sm text-muted-foreground">
          {notSetup > 0
            ? `${notSetup} course${notSetup !== 1 ? "s" : ""} need evaluation setup`
            : null}
          {notSetup > 0 && draft > 0 ? " · " : null}
          {draft > 0 ? `${draft} in draft` : null}
        </p>
      ) : null}
    </div>
  )
}

function StageNote({
  icon,
  message,
  actions,
}: {
  icon: string
  message: string
  actions?: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 items-start gap-2 text-sm leading-snug text-foreground">
        <i className={cn("fa-light mt-0.5 shrink-0 text-muted-foreground", icon)} aria-hidden="true" />
        <span>{message}</span>
      </div>
      {actions ? <div className="flex shrink-0 flex-wrap gap-1.5">{actions}</div> : null}
    </div>
  )
}

function buildStageNotes(term: EvaluationTerm, refDate: Date, product: ReturnType<typeof useProduct>["product"]): React.ReactNode[] {
  const { counts } = term
  const notes: React.ReactNode[] = []
  const setupGap = counts.notSetup + counts.draft

  if (setupGap > 0) {
    notes.push(
      <StageNote
        key="setup"
        icon="fa-clipboard-list"
        message={`${counts.notSetup} course${counts.notSetup !== 1 ? "s" : ""} need evaluation setup${counts.draft > 0 ? ` and ${counts.draft} in draft` : ""}.`}
        actions={
          <>
            <Button size="sm" variant="outline" asChild>
              <Link to={evaluationSchedulePath(product, { termId: term.id, stages: "notSetup" })}>
                Set up evaluation
              </Link>
            </Button>
            <Button size="sm" asChild>
              <Link
                to={evaluationSchedulePath(product, {
                  termId: term.id,
                  stages: "notSetup,draft",
                })}
              >
                Schedule
              </Link>
            </Button>
          </>
        }
      />,
    )
  }

  if (counts.scheduled > 0) {
    const next =
      term.nextScheduledDate != null ? daysUntil(term.nextScheduledDate, refDate) : null
    notes.push(
      <StageNote
        key="scheduled"
        icon="fa-calendar-clock"
        message={
          next != null
            ? `Evaluations for ${counts.scheduled} courses are scheduled, next one going out in ${next} days`
            : `Evaluations for ${counts.scheduled} courses are scheduled`
        }
        actions={
          <>
            <Button size="sm" variant="outline" asChild>
              <Link
                to={evaluationDistributionPath(product, {
                  termId: term.id,
                  action: "update",
                })}
              >
                Update
              </Link>
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link
                to={evaluationDistributionPath(product, {
                  termId: term.id,
                  action: "delete",
                })}
              >
                Delete
              </Link>
            </Button>
          </>
        }
      />,
    )
  }

  if (counts.live > 0) {
    const closing =
      term.nextClosingDate != null ? daysUntil(term.nextClosingDate, refDate) : null
    notes.push(
      <StageNote
        key="live"
        icon="fa-signal-stream"
        message={
          closing != null
            ? `Evaluations for ${counts.live} courses are live, closing in ${closing} days`
            : `Evaluations for ${counts.live} courses are live`
        }
        actions={
          <Button size="sm" asChild>
            <Link
              to={evaluationDistributionPath(product, {
                termId: term.id,
                action: "remind",
              })}
            >
              Remind or extend
            </Link>
          </Button>
        }
      />,
    )
  }

  if (counts.closed > 0) {
    notes.push(
      <StageNote
        key="closed"
        icon="fa-inbox"
        message={`${counts.closed} closed (${Math.round((counts.closed / term.courseOfferings) * 100)}%) · ${term.closedResponseRate != null ? `${term.closedResponseRate}%` : "None"} avg response`}
        actions={
          <Button size="sm" variant="outline" asChild>
            <Link to={evaluationReviewPath(product, term.id)}>Review feedback</Link>
          </Button>
        }
      />,
    )
  }

  return notes
}

interface TermEvaluationSummaryProps {
  term: EvaluationTerm
  /** Stage notes panel under the pipeline (TermCard default). */
  showStageNotes?: boolean
  className?: string
}

/** Shared term lifecycle summary — coverage, stage pipeline, live response. */
export function TermEvaluationSummary({
  term,
  showStageNotes = true,
  className,
}: TermEvaluationSummaryProps) {
  const { product } = useProduct()
  const refDate = EVALUATION_REFERENCE_DATE
  const { counts, courseOfferings } = term
  const coverage = evaluationCoveragePercent(counts, courseOfferings)
  const covered = evaluationCoverageCount(counts)
  const isComplete =
    counts.notSetup === 0 && counts.draft === 0 && coverage === 100
  const isAnalyticsTerm =
    term.cardCase === "all-closed" || term.cardCase === "all-published"
  const closingLabel =
    term.nextClosingDate != null
      ? `${daysUntil(term.nextClosingDate, refDate)} days to close`
      : undefined
  const stageNotes =
    showStageNotes && !isAnalyticsTerm ? buildStageNotes(term, refDate, product) : []

  if (term.cardCase === "no-courses" || term.cardCase === "no-evaluations") {
    return null
  }

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {courseOfferings > 0 ? (
        <CoverageBar
          percent={coverage}
          covered={covered}
          total={courseOfferings}
          complete={isComplete && !isAnalyticsTerm}
          notSetup={counts.notSetup}
          draft={counts.draft}
        />
      ) : null}

      <EvaluationStagePipeline counts={counts} courseOfferings={courseOfferings} />

      {!isAnalyticsTerm && counts.live > 0 && term.liveResponseRate != null ? (
        <ResponseTargetBar rate={term.liveResponseRate} closingLabel={closingLabel} />
      ) : null}

      {stageNotes.length > 0 ? (
        <div className="flex flex-col gap-2.5 rounded-lg border border-border/60 bg-muted/20 px-3 py-3">
          {stageNotes}
        </div>
      ) : null}
    </div>
  )
}
